/**
 * EmbryoReportScreen
 *
 * Displays embryo testing results modeled after real PGT-A / WGS reports
 * (CooperGenomics, Igenomix, Orchid, Herasight style).
 *
 * Data flows from `GET /cycles/:cycleId/embryo-report` via useEmbryoReport,
 * and the pipeline funnel counts come from `phase.fertilityMetrics` via
 * usePastCycleDetail.
 */

import { useMemo, useState } from "react";
import {
  View,
  Text,
  TextInput,
  ScrollView,
  Pressable,
  LayoutAnimation,
  Image,
  ActivityIndicator,
  Linking,
} from "react-native";
import {
  ChevronLeft,
  Dna,
  FlaskConical,
  Info,
  Calendar,
  Building2,
  User,
  RefreshCw,
  Pencil,
  Clock3,
  ExternalLink,
  FileText,
  Upload,
  Search,
  X,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { useSmartBack } from "../hooks/useSmartBack";
import { EMBER } from "../../onboarding/theme";
import type { CareStackParamList } from "../types";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  CardSubtitleText,
  CardTitleText,
  MoonStatBlock,
  SectionTitle,
  SoftSecondaryButton,
} from "@louise/moon-ui";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { EmbryoDetailSheet } from "@src/packages/care/cryovault/embryo-detail";
import type { EmbryoDetailData } from "@src/packages/care/cryovault/embryo-detail";
import {
  FreezingResultsFunnel,
  buildEmbryoFreezingFunnel,
} from "@src/packages/care/cryovault/FreezingResultsFunnel";
import {
  useEmbryoReport,
  useEmbryoReportHistory,
  useCreateEmbryoReport,
  useUpdateEmbryoReport,
  type EmbryoReport,
  type EmbryoGenomicResult,
  type EmbryoReportHistoryEntry,
} from "@src/packages/cycles/hooks/useEmbryoReport";
import { EmbryoReportEntryForm } from "./EmbryoReportEntryForm";
import {
  toEmbryoReportCreateInput,
  toEmbryoReportFormValues,
  toEmbryoReportUpdateInput,
  type EmbryoReportFormValues,
} from "./embryoReportEntryInput";
import { usePastCycleDetail } from "@src/packages/cycles/hooks/usePastCycleDetail";
import { useGetDownloadUrl } from "@src/packages/documents/hooks/useDocuments";
import { parseLocalDate } from "@src/packages/utils/date";
import ScreenHeader from "../components/ScreenHeader";
import { useDocumentUploadController } from "../DocumentUploadController";
import {
  filterEmbryoReportResults,
  type EmbryoResultFilter,
} from "./embryoReportFilters";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const EMBRYO_PLACEHOLDER = require("../../assets/images/embryo-placeholder.png");

type Props = NativeStackScreenProps<CareStackParamList, "EmbryoReport">;

// ── Color constants ──

const RESULT_COLORS = {
  euploid: "#4E7342",
  aneuploid: "#8B3A1A",
  mosaic: "#B08030",
  no_result: "#8A7D74",
} as const;

const RESULT_BG = {
  euploid: "rgba(78, 115, 66, 0.1)",
  aneuploid: "rgba(139, 58, 26, 0.1)",
  mosaic: "rgba(176, 128, 48, 0.1)",
  no_result: "rgba(138, 125, 116, 0.08)",
} as const;

const TRANSFER_LABEL_KEYS = {
  yes: "care.embryo_report.transfer_yes",
  no: "care.embryo_report.transfer_no",
  with_counseling: "care.embryo_report.transfer_counseling",
} as const;

const RESULT_LABEL_KEYS = {
  euploid: "care.embryo_report.result_euploid",
  aneuploid: "care.embryo_report.result_aneuploid",
  mosaic: "care.embryo_report.result_mosaic",
  no_result: "care.embryo_report.result_no_result",
} as const;

const EMBRYO_RESULT_FILTERS: EmbryoResultFilter[] = [
  "all",
  "euploid",
  "aneuploid",
  "mosaic",
  "no_result",
];

const REPORT_STATE_STYLES: Record<
  EmbryoReport["reportState"],
  { color: string; backgroundColor: string }
> = {
  pending: { color: "#9A6418", backgroundColor: "rgba(154, 100, 24, 0.12)" },
  partial: { color: "#8A5A16", backgroundColor: "rgba(176, 128, 48, 0.14)" },
  final: { color: "#4E7342", backgroundColor: "rgba(78, 115, 66, 0.12)" },
  corrected: { color: "#5D4B86", backgroundColor: "rgba(93, 75, 134, 0.12)" },
  superseded: {
    color: EMBER.colors.textLight,
    backgroundColor: "rgba(138, 125, 116, 0.12)",
  },
  retracted: { color: "#8B3A1A", backgroundColor: "rgba(139, 58, 26, 0.12)" },
};

function getReportStateLabel(
  t: TranslationFunction,
  state: EmbryoReport["reportState"],
): string {
  switch (state) {
    case "pending":
      return t("care.embryo_report.report_state_pending");
    case "partial":
      return t("care.embryo_report.report_state_partial");
    case "final":
      return t("care.embryo_report.report_state_final");
    case "corrected":
      return t("care.embryo_report.report_state_corrected");
    case "superseded":
      return t("care.embryo_report.report_state_superseded");
    case "retracted":
      return t("care.embryo_report.report_state_retracted");
  }
}

function getTestTypeLabel(
  t: TranslationFunction,
  testType: EmbryoReport["testType"],
): string {
  const keyByTestType: Record<EmbryoReport["testType"], string> = {
    not_tested: "care.embryo_report.entry.test_type_not_tested",
    undecided: "care.embryo_report.entry.test_type_undecided",
    clinic_pending: "care.embryo_report.entry.test_type_clinic_pending",
    "PGT-A": "care.embryo_report.entry.test_type_pgt_a",
    "PGT-M": "care.embryo_report.entry.test_type_pgt_m",
    "PGT-SR": "care.embryo_report.entry.test_type_pgt_sr",
    "PGT-A + PGT-M": "care.embryo_report.entry.test_type_pgt_a_pgt_m",
    "PGT-A + PGT-SR": "care.embryo_report.entry.test_type_pgt_a_pgt_sr",
    "PGT-A + PGT-M + PGT-SR":
      "care.embryo_report.entry.test_type_pgt_a_pgt_m_pgt_sr",
    "Whole Genome Sequencing": "care.embryo_report.entry.test_type_wgs",
    other: "care.embryo_report.entry.test_type_other",
  };
  return t(keyByTestType[testType]);
}

// ── Date formatting ──

function formatDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return "—";
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const d = isDateOnly ? parseLocalDate(iso) : new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}

// ── Components ──

/** Report header with clinic/lab/dates */
function ReportHeaderCard({
  report,
  t,
  locale,
  onEdit,
}: {
  report: EmbryoReport;
  t: TranslationFunction;
  locale: string;
  onEdit?: () => void;
}) {
  return (
    <View
      style={{
        ...GLASS_CARD_STYLE,
        borderRadius: 20,
        padding: 18,
        marginBottom: 20,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 14,
        }}
      >
        <View
          style={{
            paddingHorizontal: 10,
            paddingVertical: 4,
            borderRadius: 8,
            backgroundColor: "rgba(124, 58, 237, 0.1)",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{ fontSize: 11, fontWeight: "700", color: "#7C3AED" }}
          >
            {getTestTypeLabel(t, report.testType)}
          </Text>
        </View>
        <View
          style={{
            paddingHorizontal: 10,
            paddingVertical: 4,
            borderRadius: 8,
            backgroundColor:
              REPORT_STATE_STYLES[report.reportState].backgroundColor,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "700",
              color: REPORT_STATE_STYLES[report.reportState].color,
            }}
          >
            {getReportStateLabel(t, report.reportState)}
          </Text>
        </View>
      </View>

      <View style={{ gap: 10 }}>
        <MetaRow
          icon={<Calendar size={13} color={EMBER.colors.textLight} />}
          label={t("care.embryo_report.report_date")}
          value={formatDate(report.reportDate, locale)}
        />
        <MetaRow
          icon={<FlaskConical size={13} color={EMBER.colors.textLight} />}
          label={t("care.embryo_report.biopsy_date")}
          value={formatDate(report.biopsyDate, locale)}
        />
        <MetaRow
          icon={<Calendar size={13} color={EMBER.colors.textLight} />}
          label={t("care.embryo_report.retrieval_date")}
          value={formatDate(report.retrievalDate, locale)}
        />
        <MetaRow
          icon={<Building2 size={13} color={EMBER.colors.textLight} />}
          label={t("care.embryo_report.clinic")}
          value={report.referringClinic}
        />
        <MetaRow
          icon={<User size={13} color={EMBER.colors.textLight} />}
          label={t("care.embryo_report.physician")}
          value={report.referringPhysician}
        />
        <MetaRow
          icon={<Dna size={13} color={EMBER.colors.textLight} />}
          label={t("care.embryo_report.laboratory")}
          value={report.laboratory}
        />
        <MetaRow
          icon={<FlaskConical size={13} color={EMBER.colors.textLight} />}
          label={t("care.embryo_report.sample_type")}
          value={report.sampleType}
        />
      </View>

      {report.indication && (
        <View
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTopWidth: 1,
            borderTopColor: EMBER.colors.border,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "600",
              color: EMBER.colors.textFaint,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            {t("care.embryo_report.indication")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textMid,
              lineHeight: 18,
            }}
          >
            {report.indication}
          </Text>
        </View>
      )}

      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 10,
          marginTop: 14,
          paddingTop: 14,
          borderTopWidth: 1,
          borderTopColor: EMBER.colors.border,
        }}
      >
        <Info size={15} color={EMBER.colors.warm} style={{ marginTop: 1 }} />
        <View style={{ flex: 1 }}>
          <CardTitleText style={{ fontSize: 13, lineHeight: 17 }}>
            {t("care.embryo_report.review_title")}
          </CardTitleText>
          <CardSubtitleText style={{ fontSize: 12, lineHeight: 17 }}>
            {report.testType === "Whole Genome Sequencing"
              ? t("care.embryo_report.review_wgs")
              : report.testType === "not_tested" ||
                  report.testType === "undecided"
                ? t("care.embryo_report.review_no_testing")
                : report.testType === "clinic_pending"
                  ? t("care.embryo_report.review_clinic_pending")
                  : t("care.embryo_report.review_pgt")}
          </CardSubtitleText>
        </View>
      </View>

      {onEdit && (
        <ReportActionButton
          label={t("care.embryo_report.entry.edit")}
          onPress={onEdit}
          icon={<Pencil size={16} color={EMBER.colors.warm} />}
        />
      )}
    </View>
  );
}

function ReportHistoryCard({
  entries,
  t,
  locale,
}: {
  entries: EmbryoReportHistoryEntry[];
  t: TranslationFunction;
  locale: string;
}) {
  if (entries.length < 2) return null;

  return (
    <View
      style={{
        ...GLASS_CARD_STYLE,
        borderRadius: 20,
        padding: 18,
        marginBottom: 20,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <Clock3 size={16} color={EMBER.colors.warm} />
        <SectionTitle>{t("care.embryo_report.history.title")}</SectionTitle>
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 17,
          color: EMBER.colors.textLight,
          marginTop: 6,
          marginBottom: 14,
        }}
      >
        {t("care.embryo_report.history.saved_versions", {
          params: { count: String(entries.length) },
        })}
      </Text>

      <View style={{ gap: 12 }}>
        {entries.map((entry) => (
          <View
            key={entry.id}
            style={{
              gap: 5,
              paddingTop: 12,
              borderTopWidth: 1,
              borderTopColor: EMBER.colors.border,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                }}
              >
                {t("care.embryo_report.history.version_label", {
                  params: { version: String(entry.version) },
                })}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 10,
                  fontWeight: "700",
                  color: REPORT_STATE_STYLES[entry.reportState].color,
                  textTransform: "uppercase",
                }}
              >
                {getReportStateLabel(t, entry.reportState)}
              </Text>
            </View>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 11, color: EMBER.colors.textLight }}
            >
              {t("care.embryo_report.history.updated_on", {
                params: { date: formatDate(entry.updatedAt, locale) },
              })}
            </Text>
            {entry.correctionReason ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 17,
                  color: EMBER.colors.textMid,
                }}
              >
                {t("care.embryo_report.history.correction_reason_label", {
                  params: { reason: entry.correctionReason },
                })}
              </Text>
            ) : null}
          </View>
        ))}
      </View>
    </View>
  );
}

function SourceDocumentCard({
  report,
  t,
  onOpen,
  onUpload,
  isOpening,
}: {
  report: EmbryoReport;
  t: TranslationFunction;
  onOpen?: () => void;
  onUpload?: () => void;
  isOpening?: boolean;
}) {
  const upload = report.attachment?.upload;
  if (!upload && !onUpload) return null;

  return (
    <View
      style={{
        ...GLASS_CARD_STYLE,
        borderRadius: 20,
        padding: 18,
        marginBottom: 20,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
        <FileText
          size={17}
          color={EMBER.colors.warm}
          style={{ marginTop: 1 }}
        />
        <View style={{ flex: 1, gap: 4 }}>
          <SectionTitle>
            {t("care.embryo_report.source_document_heading")}
          </SectionTitle>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textLight,
            }}
          >
            {t("care.embryo_report.source_document_body")}
          </Text>
          {upload && (
            <Text
              className="font-dm-sans"
              numberOfLines={2}
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.text,
                marginTop: 4,
              }}
            >
              {upload.name ?? t("care.embryo_report.source_document_file")}
            </Text>
          )}
        </View>
      </View>

      {upload && onOpen && (
        <ReportActionButton
          label={t("care.embryo_report.open_source_document")}
          onPress={onOpen}
          disabled={isOpening}
          icon={<ExternalLink size={16} color={EMBER.colors.warm} />}
        />
      )}

      {onUpload && (
        <ReportActionButton
          label={t(
            upload
              ? "care.embryo_report.replace_source_document"
              : "care.embryo_report.upload_or_import_report",
          )}
          onPress={onUpload}
          icon={<Upload size={16} color={EMBER.colors.warm} />}
        />
      )}
    </View>
  );
}

function MetaRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
      {icon}
      <Text
        className="font-dm-sans"
        style={{ fontSize: 12, color: EMBER.colors.textLight, width: 100 }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          fontWeight: "600",
          color: EMBER.colors.text,
          flex: 1,
        }}
      >
        {value}
      </Text>
    </View>
  );
}

function SexBadge({ sex }: { sex: "XX" | "XY" }) {
  const isFemale = sex === "XX";
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 3 }}>
      <Text style={{ fontSize: 13 }}>{isFemale ? "♀" : "♂"}</Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 11, fontWeight: "600", color: EMBER.colors.textMid }}
      >
        {sex}
      </Text>
    </View>
  );
}

function SummaryTable({
  embryos,
  t,
}: {
  embryos: EmbryoGenomicResult[];
  t: TranslationFunction;
}) {
  return (
    <View
      style={{
        ...GLASS_CARD_STYLE,
        borderRadius: 20,
        padding: 0,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          paddingHorizontal: 14,
          paddingVertical: 10,
          backgroundColor: "rgba(0,0,0,0.03)",
        }}
      >
        <Text style={[TH, { width: 32 }]}>#</Text>
        <Text style={[TH, { width: 50 }]}>
          {t("care.embryo_report.col_grade")}
        </Text>
        <Text style={[TH, { flex: 1 }]}>
          {t("care.embryo_report.col_karyotype")}
        </Text>
        <Text style={[TH, { width: 38, textAlign: "center" }]}>
          {t("care.embryo_report.col_sex")}
        </Text>
        <Text style={[TH, { width: 80, textAlign: "right" }]}>
          {t("care.embryo_report.col_result")}
        </Text>
      </View>

      {embryos.map((e, i) => (
        <View
          key={e.id}
          style={{
            flexDirection: "row",
            alignItems: "center",
            paddingHorizontal: 14,
            paddingVertical: 11,
            backgroundColor: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              width: 32,
              fontSize: 13,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {e.embryoNumber}
          </Text>
          <Text
            className="font-dm-sans"
            style={{ width: 50, fontSize: 12, color: EMBER.colors.textMid }}
          >
            {e.grade ?? "—"}
          </Text>
          <Text
            className="font-dm-mono"
            style={{
              flex: 1,
              fontSize: 11,
              fontWeight: "500",
              color:
                e.result === "euploid"
                  ? EMBER.colors.text
                  : RESULT_COLORS[e.result],
            }}
          >
            {e.karyotype}
          </Text>
          <View style={{ width: 38, alignItems: "center" }}>
            <SexBadge sex={e.sex} />
          </View>
          <View style={{ width: 80, alignItems: "flex-end" }}>
            <View
              style={{
                paddingHorizontal: 8,
                paddingVertical: 3,
                borderRadius: 6,
                backgroundColor: RESULT_BG[e.result],
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 10,
                  fontWeight: "700",
                  color: RESULT_COLORS[e.result],
                  textTransform: "uppercase",
                }}
              >
                {t(RESULT_LABEL_KEYS[e.result])}
              </Text>
            </View>
          </View>
        </View>
      ))}
    </View>
  );
}

const TH = {
  fontSize: 10,
  fontWeight: "600" as const,
  color: EMBER.colors.textFaint,
  textTransform: "uppercase" as const,
  letterSpacing: 0.5,
};

function EmbryoTile({
  embryo,
  isSelected,
  onPress,
  t,
}: {
  embryo: EmbryoGenomicResult;
  isSelected: boolean;
  onPress: () => void;
  t: TranslationFunction;
}) {
  const capturePress = usePressCapture("EmbryoTile");
  const resultColor = RESULT_COLORS[embryo.result];
  const resultBg = RESULT_BG[embryo.result];
  const hasExtended = !!embryo.genomicsProvider;

  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(
            t("care.embryo_report.embryo_number", {
              params: { number: String(embryo.embryoNumber) },
            }),
          ),
          "embryo_tile",
        );
        onPress();
      }}
      style={{
        flex: 1,
        ...GLASS_CARD_STYLE,
        borderRadius: 16,
        padding: 0,
        overflow: "hidden",
        borderColor: isSelected
          ? `${resultColor}50`
          : "rgba(255, 255, 255, 0.5)",
        borderWidth: isSelected ? 2 : 1,
      }}
    >
      <View
        style={{
          height: 100,
          backgroundColor: "rgba(0,0,0,0.03)",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Image
          source={EMBRYO_PLACEHOLDER}
          style={{ width: 70, height: 70, borderRadius: 35, opacity: 0.7 }}
          resizeMode="cover"
        />
        <View
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            paddingHorizontal: 6,
            paddingVertical: 2,
            borderRadius: 5,
            backgroundColor: resultBg,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 9,
              fontWeight: "700",
              color: resultColor,
              textTransform: "uppercase",
            }}
          >
            {embryo.result === "no_result"
              ? t("care.embryo_report.result_na")
              : t(RESULT_LABEL_KEYS[embryo.result])}
          </Text>
        </View>
        <View style={{ position: "absolute", top: 8, left: 8 }}>
          <Text style={{ fontSize: 14 }}>
            {embryo.sex === "XX" ? "♀" : "♂"}
          </Text>
        </View>
        {hasExtended && (
          <View
            style={{
              position: "absolute",
              bottom: 8,
              right: 8,
              paddingHorizontal: 5,
              paddingVertical: 1,
              borderRadius: 4,
              backgroundColor: "rgba(124, 58, 237, 0.12)",
            }}
          >
            <Text style={{ fontSize: 8, fontWeight: "700", color: "#7C3AED" }}>
              WGS
            </Text>
          </View>
        )}
      </View>

      <View style={{ padding: 12 }}>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 14, fontWeight: "700", color: EMBER.colors.text }}
        >
          {t("care.embryo_report.embryo_number", {
            params: { number: String(embryo.embryoNumber) },
          })}
        </Text>
        {embryo.grade && (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              color: EMBER.colors.textLight,
              marginTop: 2,
            }}
          >
            {embryo.biopsyDay != null
              ? t("care.embryo_report.grade_day", {
                  params: {
                    grade: embryo.grade,
                    day: String(embryo.biopsyDay),
                  },
                })
              : embryo.grade}
          </Text>
        )}
        <Text
          className="font-dm-mono"
          style={{
            fontSize: 10,
            fontWeight: "500",
            color: resultColor,
            marginTop: 3,
          }}
        >
          {embryo.karyotype}
        </Text>
      </View>
    </Pressable>
  );
}

/** Map API embryo record + report context to the shared detail-sheet shape.
 * Only fields that are actually present on the embryo-report endpoint are
 * forwarded — the sheet hides any section whose data is missing. Real
 * specimen status / storage / transfer / outcome data lives on the linked
 * embryoBiopsy → specimen records and isn't part of this endpoint, so those
 * sections intentionally stay hidden until we wire a richer query. */
function toDetailData(
  e: EmbryoGenomicResult,
  report: EmbryoReport,
  t: TranslationFunction,
): EmbryoDetailData {
  return {
    embryoNumber: e.embryoNumber,
    sex: e.sex,
    grade: e.grade ?? undefined,
    embryoId: e.sampleId,

    dayOfDevelopment: e.biopsyDay != null ? `Day ${e.biopsyDay}` : undefined,

    biopsyDay: e.biopsyDay ?? undefined,
    biopsyDate: e.biopsyDate ?? undefined,
    karyotype: e.karyotype,
    result: e.result,
    transferRecommendation: e.transferRecommendation,
    pgtPerformed: getTestTypeLabel(t, report.testType),
    cnProfile: e.cnProfile ?? undefined,
    affectedChromosomes: e.affectedChromosomes ?? undefined,
    aneuploidyImpact: e.aneuploidyImpact ?? undefined,
    mosaicism: e.mosaicism ?? undefined,
    labComments: e.comments ?? undefined,
    developmentObservations:
      e.developmentObservations.length > 0
        ? e.developmentObservations
        : undefined,

    monogenicFindings:
      e.monogenicFindings.length > 0
        ? e.monogenicFindings.map((f) => ({
            condition: f.condition,
            gene: f.gene,
            status: f.status,
            inheritancePattern: f.inheritancePattern,
            clinicalSignificance: f.clinicalSignificance ?? undefined,
          }))
        : undefined,
    polygenicRisks:
      e.polygenicRisks.length > 0
        ? e.polygenicRisks.map((r) => ({
            condition: r.condition,
            category: r.category,
            percentile: r.percentile,
            absoluteRisk: r.absoluteRisk,
            populationRisk: r.populationRisk,
            isElevated: r.isElevated,
            ciLow: r.ciLow ?? undefined,
            ciHigh: r.ciHigh ?? undefined,
          }))
        : undefined,
    genomicsProvider: e.genomicsProvider ?? undefined,
  };
}

function ComparisonTable({
  embryos,
  t,
}: {
  embryos: EmbryoGenomicResult[];
  t: TranslationFunction;
}) {
  const COL_W = 90;
  const LABEL_W = 120;

  // "WGS performed" signal — genomicsProvider is only set for WGS/expanded tests
  const carrierSummaries = embryos.map((e) => {
    if (!e.genomicsProvider) return null;
    const carriers = e.monogenicFindings.filter((f) => f.status === "carrier");
    const affected = e.monogenicFindings.filter((f) => f.status === "affected");
    if (affected.length > 0) return affected.map((f) => f.gene).join(", ");
    if (carriers.length > 0) return carriers.map((f) => f.gene).join(", ");
    return "__clear__";
  });

  const prsSummaries = embryos.map((e) => {
    if (!e.genomicsProvider) return null;
    const elevated = e.polygenicRisks.filter((r) => r.isElevated);
    if (elevated.length > 0) {
      return elevated.map((r) => r.condition.split(" ")[0]).join(", ");
    }
    const high = e.polygenicRisks.filter((r) => r.percentile > 75);
    if (high.length > 0) {
      return t("care.embryo_report.above_avg", {
        params: { count: String(high.length) },
      });
    }
    return "__typical__";
  });

  type RowDef = {
    label: string;
    values: (string | { text: string; color?: string; bg?: string })[];
  };

  const rows: RowDef[] = [
    {
      label: t("care.embryo_report.col_result"),
      values: embryos.map((e) => ({
        text:
          e.result === "no_result"
            ? t("care.embryo_report.result_na")
            : t(RESULT_LABEL_KEYS[e.result]),
        color: RESULT_COLORS[e.result],
        bg: RESULT_BG[e.result],
      })),
    },
    {
      label: t("care.embryo_report.col_grade"),
      values: embryos.map((e) => e.grade ?? "—"),
    },
    {
      label: t("care.embryo_report.col_biopsy_day"),
      values: embryos.map((e) =>
        e.biopsyDay != null
          ? t("care.embryo_report.day_label", {
              params: { day: String(e.biopsyDay) },
            })
          : "—",
      ),
    },
    {
      label: t("care.embryo_report.col_sex"),
      values: embryos.map((e) => `${e.sex === "XX" ? "♀" : "♂"} ${e.sex}`),
    },
    {
      label: t("care.embryo_report.col_karyotype"),
      values: embryos.map((e) => ({
        text: e.karyotype,
        color:
          e.result !== "euploid" ? RESULT_COLORS[e.result] : EMBER.colors.text,
      })),
    },
    {
      label: t("care.embryo_report.col_transfer"),
      values: embryos.map((e) => ({
        text: t(TRANSFER_LABEL_KEYS[e.transferRecommendation]),
        color:
          RESULT_COLORS[
            e.transferRecommendation === "yes"
              ? "euploid"
              : e.transferRecommendation === "no"
                ? "aneuploid"
                : "mosaic"
          ],
      })),
    },
  ];

  if (
    embryos.some(
      (e) => e.affectedChromosomes && e.affectedChromosomes.length > 0,
    )
  ) {
    rows.push({
      label: t("care.embryo_report.col_abnormalities"),
      values: embryos.map((e) => {
        if (!e.affectedChromosomes || e.affectedChromosomes.length === 0) {
          return "—";
        }
        return {
          text: e.affectedChromosomes.join(", "),
          color: RESULT_COLORS[e.result],
        };
      }),
    });
  }

  if (embryos.some((e) => e.mosaicism && e.mosaicism.length > 0)) {
    rows.push({
      label: t("care.embryo_report.col_mosaicism"),
      values: embryos.map((e) => {
        if (!e.mosaicism || e.mosaicism.length === 0) return "—";
        return {
          text: e.mosaicism
            .map((m) => `Chr${m.chromosome} ~${m.level}%`)
            .join(", "),
          color: RESULT_COLORS.mosaic,
        };
      }),
    });
  }

  if (carrierSummaries.some((s) => s !== null)) {
    rows.push({
      label: t("care.embryo_report.col_carrier_status"),
      values: carrierSummaries.map((s, i) => {
        if (s === null) return "—";
        if (s === "__clear__") {
          return {
            text: t("care.embryo_report.carrier_clear"),
            color: RESULT_COLORS.euploid,
          };
        }
        const embryo = embryos[i]!;
        const hasAffected = embryo.monogenicFindings.some(
          (f) => f.status === "affected",
        );
        return {
          text: s,
          color: hasAffected ? RESULT_COLORS.aneuploid : RESULT_COLORS.mosaic,
        };
      }),
    });
  }

  if (prsSummaries.some((s) => s !== null)) {
    rows.push({
      label: t("care.embryo_report.col_polygenic_risk"),
      values: prsSummaries.map((s) => {
        if (s === null) return "—";
        if (s === "__typical__") {
          return {
            text: t("care.embryo_report.carrier_typical"),
            color: RESULT_COLORS.euploid,
          };
        }
        return { text: s, color: RESULT_COLORS.mosaic };
      }),
    });
  }

  return (
    <View
      style={{
        ...GLASS_CARD_STYLE,
        borderRadius: 20,
        padding: 0,
        overflow: "hidden",
      }}
    >
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View>
          <View
            style={{
              flexDirection: "row",
              backgroundColor: "rgba(0,0,0,0.03)",
            }}
          >
            <View
              style={{
                width: LABEL_W,
                paddingHorizontal: 14,
                paddingVertical: 10,
                justifyContent: "center",
              }}
            >
              <Text style={[TH]}>{t("care.embryo_report.col_embryo")}</Text>
            </View>
            {embryos.map((e) => (
              <View
                key={e.id}
                style={{
                  width: COL_W,
                  paddingVertical: 10,
                  alignItems: "center",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                  }}
                >
                  #{e.embryoNumber}
                </Text>
              </View>
            ))}
          </View>

          {rows.map((row, ri) => (
            <View
              key={row.label}
              style={{
                flexDirection: "row",
                backgroundColor:
                  ri % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
              }}
            >
              <View
                style={{
                  width: LABEL_W,
                  paddingHorizontal: 14,
                  paddingVertical: 10,
                  justifyContent: "center",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    fontWeight: "600",
                    color: EMBER.colors.textLight,
                  }}
                >
                  {row.label}
                </Text>
              </View>
              {row.values.map((val, ci) => {
                const isObj = typeof val === "object";
                const text = isObj ? val.text : val;
                const color = isObj ? val.color : EMBER.colors.textMid;
                const bg = isObj && val.bg ? val.bg : undefined;

                return (
                  <View
                    key={ci}
                    style={{
                      width: COL_W,
                      paddingVertical: 10,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {bg ? (
                      <View
                        style={{
                          paddingHorizontal: 6,
                          paddingVertical: 2,
                          borderRadius: 5,
                          backgroundColor: bg,
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 10,
                            fontWeight: "700",
                            color,
                            textTransform: "uppercase",
                          }}
                        >
                          {text}
                        </Text>
                      </View>
                    ) : (
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 11,
                          fontWeight: "500",
                          color,
                          textAlign: "center",
                        }}
                      >
                        {text}
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
}

function EmbryoGrid({
  embryos,
  selectedIndex,
  onSelect,
  t,
}: {
  embryos: EmbryoGenomicResult[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  t: TranslationFunction;
}) {
  const rows: EmbryoGenomicResult[][] = [];
  for (let i = 0; i < embryos.length; i += 2) {
    rows.push(embryos.slice(i, i + 2));
  }

  return (
    <View style={{ gap: 10 }}>
      {rows.map((row, ri) => (
        <View key={ri} style={{ flexDirection: "row", gap: 10 }}>
          {row.map((embryo) => {
            const idx = embryos.indexOf(embryo);
            return (
              <EmbryoTile
                key={embryo.id}
                embryo={embryo}
                isSelected={selectedIndex === idx}
                onPress={() => onSelect(idx)}
                t={t}
              />
            );
          })}
          {row.length === 1 && <View style={{ flex: 1 }} />}
        </View>
      ))}
    </View>
  );
}

// ── Back button ──

function BackButton({
  onPress,
  t,
}: {
  onPress: () => void;
  t: TranslationFunction;
}) {
  const capturePress = usePressCapture("EmbryoReportBackButton");
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t("care.overview.screen_title")}
      onPress={() => {
        capturePress(
          pressLabel(t("care.overview.screen_title")),
          "care.overview.screen_title",
        );
        onPress();
      }}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        marginBottom: 8,
      }}
    >
      <ChevronLeft size={18} color={EMBER.colors.warm} strokeWidth={1.5} />
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.warm }}
      >
        {t("care.overview.screen_title")}
      </Text>
    </Pressable>
  );
}

function ReportActionButton({
  label,
  onPress,
  icon,
  disabled = false,
}: {
  label: string;
  onPress: () => void;
  icon: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <SoftSecondaryButton
      label={label}
      onPress={onPress}
      disabled={disabled}
      leading={icon}
      style={{ marginTop: 16 }}
    />
  );
}

function EmbryoReportPageHeader({
  onBack,
  t,
}: {
  onBack: () => void;
  t: TranslationFunction;
}) {
  return (
    <>
      <BackButton onPress={onBack} t={t} />
      <ScreenHeader
        title={t("care.embryo_report.screen_title")}
        subtitle={t("care.embryo_report.screen_intro")}
      />
    </>
  );
}

function EmbryoCollectionFilters({
  query,
  resultFilter,
  onQueryChange,
  onResultFilterChange,
  t,
}: {
  query: string;
  resultFilter: EmbryoResultFilter;
  onQueryChange: (query: string) => void;
  onResultFilterChange: (filter: EmbryoResultFilter) => void;
  t: TranslationFunction;
}) {
  const capturePress = usePressCapture("EmbryoCollectionFilters");
  return (
    <View
      style={{
        ...GLASS_CARD_STYLE,
        borderRadius: 18,
        padding: 14,
        marginBottom: 16,
        gap: 10,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          color: EMBER.colors.text,
          fontSize: 13,
          fontWeight: "700",
        }}
      >
        {t("care.embryo_report.embryo_search_label")}
      </Text>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          borderWidth: 1,
          borderColor: "rgba(68, 45, 33, 0.16)",
          borderRadius: 12,
          minHeight: 44,
          paddingLeft: 12,
          paddingRight: 8,
          backgroundColor: "rgba(255, 252, 248, 0.72)",
        }}
      >
        <Search size={16} color={EMBER.colors.textLight} />
        <TextInput
          accessibilityLabel={t("care.embryo_report.embryo_search_label")}
          autoCapitalize="none"
          autoCorrect={false}
          onChangeText={onQueryChange}
          placeholder={t("care.embryo_report.embryo_search_placeholder")}
          placeholderTextColor={EMBER.colors.textLight}
          style={{
            flex: 1,
            minWidth: 0,
            paddingHorizontal: 9,
            paddingVertical: 10,
            color: EMBER.colors.text,
            fontSize: 13,
          }}
          value={query}
        />
        {query.length > 0 && (
          <Pressable
            accessibilityLabel={t("care.embryo_report.embryo_search_clear")}
            accessibilityRole="button"
            hitSlop={8}
            onPress={() => {
              // Icon-only control; its accessibility label is the copy.
              capturePress(
                pressLabel(t("care.embryo_report.embryo_search_clear")),
                "care.embryo_report.embryo_search_clear",
              );
              onQueryChange("");
            }}
          >
            <X size={16} color={EMBER.colors.textLight} />
          </Pressable>
        )}
      </View>
      <Text
        className="font-dm-sans"
        style={{ color: EMBER.colors.textLight, fontSize: 11 }}
      >
        {t("care.embryo_report.embryo_filter_result")}
      </Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={{ flexDirection: "row", gap: 8 }}>
          {EMBRYO_RESULT_FILTERS.map((filter) => {
            const isSelected = resultFilter === filter;
            const label =
              filter === "all"
                ? t("care.embryo_report.embryo_filter_all")
                : t(RESULT_LABEL_KEYS[filter]);
            return (
              <Pressable
                accessibilityLabel={label}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                key={filter}
                onPress={() => {
                  capturePress(pressLabel(label), "embryo_result_filter");
                  onResultFilterChange(filter);
                }}
                style={{
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: isSelected
                    ? EMBER.colors.warm
                    : "rgba(68, 45, 33, 0.16)",
                  backgroundColor: isSelected
                    ? "rgba(139, 58, 26, 0.10)"
                    : "rgba(255, 252, 248, 0.72)",
                  paddingHorizontal: 12,
                  paddingVertical: 7,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    color: isSelected ? EMBER.colors.warm : EMBER.colors.text,
                    fontSize: 11,
                    fontWeight: isSelected ? "700" : "500",
                  }}
                >
                  {label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
}

// ── Main screen ──

export default function EmbryoReportScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const handleBack = useSmartBack({
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const { locale } = useLocale();
  const { cycleId } = route.params;
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [embryoSearchQuery, setEmbryoSearchQuery] = useState("");
  const [embryoResultFilter, setEmbryoResultFilter] =
    useState<EmbryoResultFilter>("all");
  const [entryMode, setEntryMode] = useState<"create" | "edit" | null>(null);
  const [pendingAttachmentId, setPendingAttachmentId] = useState<string | null>(
    null,
  );
  const [pendingAttachmentName, setPendingAttachmentName] = useState<
    string | null
  >(null);
  const { openDocumentUpload } = useDocumentUploadController();
  const getDownloadUrl = useGetDownloadUrl();

  const {
    data: report,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useEmbryoReport(cycleId);
  const { data: reportHistory = [] } = useEmbryoReportHistory(
    report ? cycleId : undefined,
  );
  const createReport = useCreateEmbryoReport();
  const updateReport = useUpdateEmbryoReport();
  const { cycle } = usePastCycleDetail(cycleId);

  const openSourceUpload = (mode: "create" | "edit") => {
    openDocumentUpload({
      defaultCategory: "medical_records",
      onSave: ({ fileName, uploadId }) => {
        if (!uploadId) return;
        setPendingAttachmentId(uploadId);
        setPendingAttachmentName(fileName || null);
        setEntryMode(mode);
      },
    });
  };

  const openSourceDocument = async (
    disposition: "inline" | "attachment" = "inline",
  ) => {
    const uploadId = report?.attachment?.uploadId;
    if (!uploadId) return;

    try {
      const url = await getDownloadUrl.mutateAsync({
        uploadId,
        disposition,
      });
      if (url) await Linking.openURL(url);
    } catch (error) {
      console.error("Failed to access embryo report source document", error);
    }
  };

  const handleReportSubmit = (values: EmbryoReportFormValues) => {
    if (!entryMode) return;

    const onSuccess = () => {
      setEntryMode(null);
      setPendingAttachmentId(null);
      setPendingAttachmentName(null);
      void refetch();
    };

    if (entryMode === "edit") {
      const data = toEmbryoReportUpdateInput(values);
      if (!data) return;
      updateReport.mutate({ cycleId, data }, { onSuccess });
      return;
    }

    const data = toEmbryoReportCreateInput({
      ...values,
      attachmentId: pendingAttachmentId,
    });
    if (!data) return;
    createReport.mutate({ cycleId, data }, { onSuccess });
  };

  const cancelReportEntry = () => {
    createReport.reset();
    updateReport.reset();
    setEntryMode(null);
    setPendingAttachmentId(null);
    setPendingAttachmentName(null);
  };

  const embryos = useMemo(() => report?.embryos ?? [], [report]);
  const filteredEmbryos = useMemo(
    () =>
      filterEmbryoReportResults(embryos, embryoSearchQuery, embryoResultFilter),
    [embryoResultFilter, embryoSearchQuery, embryos],
  );
  const expandedEmbryo =
    expandedIndex !== null ? (filteredEmbryos[expandedIndex] ?? null) : null;

  const stats = useMemo(() => {
    const total = embryos.length;
    const euploid = embryos.filter((e) => e.result === "euploid").length;
    const aneuploid = embryos.filter((e) => e.result === "aneuploid").length;
    const mosaic = embryos.filter((e) => e.result === "mosaic").length;
    const noResult = embryos.filter((e) => e.result === "no_result").length;
    return { total, euploid, aneuploid, mosaic, noResult };
  }, [embryos]);

  const funnelStages = useMemo(() => {
    const byLabel = Object.fromEntries(
      (cycle?.resultsFunnel ?? []).map((r) => [r.label, r.value]),
    );
    return buildEmbryoFreezingFunnel({
      eggsCollected: byLabel["Eggs Retrieved"],
      matureEggs: byLabel["Mature Eggs (M2)"],
      fertilized: byLabel["Fertilized"],
      day3Embryos: byLabel["Day 3 embryos"],
      blastocysts: byLabel["Blastocysts"],
      frozenEmbryos: byLabel["Embryos Frozen"],
      euploid: report && report.embryos.length > 0 ? stats.euploid : undefined,
      noResult:
        report && report.embryos.length > 0 ? stats.noResult : undefined,
    });
  }, [cycle, report, stats.euploid, stats.noResult]);

  const handleToggle = (index: number) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleEmbryoSearchChange = (query: string) => {
    setExpandedIndex(null);
    setEmbryoSearchQuery(query);
  };

  const handleEmbryoResultFilterChange = (filter: EmbryoResultFilter) => {
    setExpandedIndex(null);
    setEmbryoResultFilter(filter);
  };

  const scrollContainerStyle = {
    paddingHorizontal: 16,
    paddingTop: SCREEN_PADDING_TOP,
    paddingBottom: 100,
    maxWidth: 520,
    width: "100%" as const,
    alignSelf: "center" as const,
  };

  if (isLoading) {
    return (
      <GlassScreenLayout>
        <ScrollView contentContainerStyle={scrollContainerStyle}>
          <EmbryoReportPageHeader onBack={handleBack} t={t} />
          <View style={{ paddingVertical: 80, alignItems: "center" }}>
            <ActivityIndicator color={EMBER.colors.warm} />
          </View>
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  if (error) {
    return (
      <GlassScreenLayout>
        <ScrollView contentContainerStyle={scrollContainerStyle}>
          <EmbryoReportPageHeader onBack={handleBack} t={t} />
          <View
            style={{
              ...GLASS_CARD_STYLE,
              borderRadius: 20,
              padding: 24,
              alignItems: "center",
            }}
          >
            <Info size={32} color={EMBER.colors.warm} strokeWidth={1.5} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                fontWeight: "600",
                color: EMBER.colors.text,
                marginTop: 12,
                textAlign: "center",
              }}
            >
              {t("care.embryo_report.loading_failed_title")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textLight,
                marginTop: 6,
                textAlign: "center",
                lineHeight: 17,
              }}
            >
              {t("care.embryo_report.loading_failed_body")}
            </Text>
            <ReportActionButton
              label={t("care.embryo_report.retry_report")}
              onPress={() => void refetch()}
              disabled={isFetching}
              icon={<RefreshCw size={16} color={EMBER.colors.warm} />}
            />
          </View>
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  if (!report) {
    return (
      <GlassScreenLayout>
        <ScrollView contentContainerStyle={scrollContainerStyle}>
          <EmbryoReportPageHeader onBack={handleBack} t={t} />
          {entryMode === "create" ? (
            <EmbryoReportEntryForm
              onCancel={cancelReportEntry}
              onSubmit={handleReportSubmit}
              isSubmitting={createReport.isPending}
              sourceDocumentName={pendingAttachmentName}
              errorMessage={
                createReport.error instanceof Error
                  ? createReport.error.message
                  : null
              }
            />
          ) : (
            <View
              style={{
                ...GLASS_CARD_STYLE,
                borderRadius: 20,
                padding: 24,
                alignItems: "center",
              }}
            >
              <Dna size={32} color={EMBER.colors.textLight} strokeWidth={1.5} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 15,
                  fontWeight: "600",
                  color: EMBER.colors.text,
                  marginTop: 12,
                  textAlign: "center",
                }}
              >
                {t("care.embryo_report.empty_title")}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  color: EMBER.colors.textLight,
                  marginTop: 6,
                  textAlign: "center",
                  lineHeight: 17,
                }}
              >
                {t("care.embryo_report.empty_body")}
              </Text>
              <ReportActionButton
                label={t("care.embryo_report.upload_or_import_report")}
                onPress={() => openSourceUpload("create")}
                icon={<Upload size={16} color={EMBER.colors.warm} />}
              />
              <ReportActionButton
                label={t("care.embryo_report.entry.open_form")}
                onPress={() => setEntryMode("create")}
                icon={<Dna size={16} color={EMBER.colors.warm} />}
              />
            </View>
          )}
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  if (entryMode === "edit") {
    return (
      <GlassScreenLayout>
        <ScrollView contentContainerStyle={scrollContainerStyle}>
          <EmbryoReportPageHeader onBack={handleBack} t={t} />
          <EmbryoReportEntryForm
            mode="edit"
            initialValues={{
              ...toEmbryoReportFormValues(report),
              attachmentId:
                pendingAttachmentId ??
                toEmbryoReportFormValues(report).attachmentId,
            }}
            onCancel={cancelReportEntry}
            onSubmit={handleReportSubmit}
            isSubmitting={updateReport.isPending}
            sourceDocumentName={pendingAttachmentName}
            errorMessage={
              updateReport.error instanceof Error
                ? updateReport.error.message
                : null
            }
          />
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={scrollContainerStyle}
        showsVerticalScrollIndicator={false}
      >
        <EmbryoReportPageHeader onBack={handleBack} t={t} />

        <ReportHeaderCard
          report={report}
          t={t}
          locale={locale}
          onEdit={
            report.ownership === "patient"
              ? () => setEntryMode("edit")
              : undefined
          }
        />

        <SourceDocumentCard
          report={report}
          t={t}
          onOpen={
            report.attachment?.upload
              ? () => void openSourceDocument("inline")
              : undefined
          }
          isOpening={getDownloadUrl.isPending}
          onUpload={
            report.ownership === "patient"
              ? () => openSourceUpload("edit")
              : undefined
          }
        />

        <ReportHistoryCard entries={reportHistory} t={t} locale={locale} />

        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 10,
            marginBottom: 20,
          }}
        >
          <StatPill
            value={stats.euploid}
            total={stats.total}
            label={t("care.embryo_report.result_euploid")}
            caption={t("care.embryo_report.of_total", {
              params: { total: String(stats.total) },
            })}
            color={RESULT_COLORS.euploid}
          />
          <StatPill
            value={stats.mosaic}
            total={stats.total}
            label={t("care.embryo_report.result_mosaic")}
            caption={t("care.embryo_report.of_total", {
              params: { total: String(stats.total) },
            })}
            color={RESULT_COLORS.mosaic}
          />
          <StatPill
            value={stats.aneuploid}
            total={stats.total}
            label={t("care.embryo_report.result_aneuploid")}
            caption={t("care.embryo_report.of_total", {
              params: { total: String(stats.total) },
            })}
            color={RESULT_COLORS.aneuploid}
          />
          {stats.noResult > 0 && (
            <StatPill
              value={stats.noResult}
              total={stats.total}
              label={t("care.embryo_report.result_no_result")}
              caption={t("care.embryo_report.of_total", {
                params: { total: String(stats.total) },
              })}
              color={RESULT_COLORS.no_result}
            />
          )}
        </View>

        {funnelStages.length > 0 && (
          <View style={{ marginBottom: 8 }}>
            <FreezingResultsFunnel stages={funnelStages} />
          </View>
        )}

        {embryos.length > 0 && (
          <>
            <EmbryoCollectionFilters
              query={embryoSearchQuery}
              resultFilter={embryoResultFilter}
              onQueryChange={handleEmbryoSearchChange}
              onResultFilterChange={handleEmbryoResultFilterChange}
              t={t}
            />
            <View style={{ marginTop: 28, marginBottom: 12 }}>
              <SectionTitle>
                {t("care.embryo_report.section_summary")}
              </SectionTitle>
            </View>
            <SummaryTable embryos={embryos} t={t} />

            <View style={{ marginTop: 28, marginBottom: 12 }}>
              <SectionTitle>
                {t("care.embryo_report.section_comparison")}
              </SectionTitle>
            </View>
            <ComparisonTable embryos={embryos} t={t} />

            <View style={{ marginTop: 28, marginBottom: 12 }}>
              <SectionTitle>
                {t("care.embryo_report.section_embryos")}
              </SectionTitle>
            </View>
            {filteredEmbryos.length > 0 ? (
              <EmbryoGrid
                embryos={filteredEmbryos}
                selectedIndex={expandedIndex}
                onSelect={(index) => handleToggle(index)}
                t={t}
              />
            ) : (
              <View
                style={{
                  ...GLASS_CARD_STYLE,
                  borderRadius: 18,
                  padding: 18,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    color: EMBER.colors.textLight,
                    fontSize: 13,
                    lineHeight: 19,
                  }}
                >
                  {t("care.embryo_report.embryo_filter_empty")}
                </Text>
              </View>
            )}
          </>
        )}

        <EmbryoDetailSheet
          open={expandedIndex !== null}
          onClose={() => setExpandedIndex(null)}
          embryo={
            expandedIndex !== null && filteredEmbryos[expandedIndex]
              ? toDetailData(filteredEmbryos[expandedIndex], report, t)
              : null
          }
          onDownloadReport={
            report.attachment?.upload
              ? () => void openSourceDocument("attachment")
              : undefined
          }
          onNavigateToCryoVault={
            expandedEmbryo?.embryoSpecimenId
              ? () => {
                  const specimenId = expandedEmbryo.embryoSpecimenId;
                  if (!specimenId) return;
                  setExpandedIndex(null);
                  navigation.navigate("CryoVault", { specimenId });
                }
              : undefined
          }
        />
      </ScrollView>
    </GlassScreenLayout>
  );
}

function StatPill({
  value,
  label,
  caption,
  color,
}: {
  value: number;
  total: number;
  label: string;
  caption: string;
  color: string;
}) {
  return (
    <View
      style={{
        ...GLASS_CARD_STYLE,
        flex: 1,
        borderRadius: 18,
        padding: 14,
        alignItems: "center",
      }}
    >
      <MoonStatBlock
        value={value}
        label={label}
        caption={caption}
        accentColor={color}
        align="center"
      />
    </View>
  );
}
